<script setup lang="ts">
import { useDates } from "@/stories/utils/useDates";
import type { DatePresets, RRuleState } from "@/types";
import { computed, ref, toRefs } from "vue";
import { toDate } from "@/stories/utils/dates";
import { useRRuleTypeString } from "@/composables/useRRuleTypeString";
import isValidRRule from "@/helpers/rrules/isValidRRule";

interface Props {
  state: RRuleState;
}

const props = defineProps<Props>();

const { format } = useDates();

const { state } = toRefs(props);

const { typeString } = useRRuleTypeString(state);

const formatSafely = (dateString: string | undefined, preset: DatePresets) => {
  if (!dateString) {
    return;
  }

  const date = new Date(dateString);

  if (state.value.startDate) {
    const originalStartDate = toDate(state.value.startDate);
    date.setHours(originalStartDate.getHours());
    date.setMinutes(originalStartDate.getMinutes());
  }

  // We should show the dates correctly. This is a fix for +2 hours.
  return format(preset, date, undefined);
};

const startDate = computed(() =>
  formatSafely((props.state.occurrences?.data || []).at(0), "long-date-time"),
);

const endDate = computed(() =>
  formatSafely((props.state.occurrences?.data || []).at(-1), "long-date-time"),
);

const isInfinite = computed(() => {
  return props.state.occurrences?.status === "infinite";
});

const translationKey = computed(() => {
  if (isInfinite.value) {
    return "templates.rruleinput.infinite_message";
  }

  return "templates.rruleinput.message";
});

const isValid = computed(() => isValidRRule(state.value.occurrences));
</script>

<template>
  <i18n-t v-if="isValid" :keypath="translationKey" tag="p" class="b300">
    <template v-slot:number>
      {{ state.occurence }}
    </template>
    <template v-slot:type> {{ typeString.toLocaleLowerCase() }} </template>
    <template v-if="!isInfinite" v-slot:startDate>
      <span class="h300">{{ startDate }}</span>
    </template>
    <template v-if="!isInfinite" v-slot:endDate>
      <span class="h300">{{ endDate }}</span>
    </template>
  </i18n-t>
</template>
